import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AREAS, EVENT_STATUS, formatDate } from "@/lib/constants";
import { Pencil, Trash2, Copy, Plus, X, Users, Download } from "lucide-react";

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const empty = {
  title: "", subtitle: "", slug: "", cover_image: "", project_id: "", area: "famiglie",
  start_date: "", end_date: "", location: "", address: "", map_url: "",
  short_desc: "", full_desc: "", program: "", audience: "", accessibility: "",
  is_free: true, price: 0, capacity: 20, booking_open_at: "", booking_close_at: "",
  contact_email: "", contact_phone: "", status: "bozza", featured: false, partners: [], attachments: [],
};

export default function EventsAdmin() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/events?limit=500").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  const save = async () => {
    if (!editing.title || !editing.slug || !editing.start_date) return toast.error("Titolo, slug e data sono obbligatori");
    const payload = { ...editing, price: parseFloat(editing.price || 0), capacity: parseInt(editing.capacity || 0) };
    if (!payload.project_id) payload.project_id = null;
    try {
      if (editing.id) await api.put(`/admin/events/${editing.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success("Evento salvato");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (id) => {
    if (!confirm("Eliminare questo evento? Verranno rimosse anche le prenotazioni.")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Eliminato"); load();
  };

  const duplicate = async (id) => {
    await api.post(`/admin/events/${id}/duplicate`);
    toast.success("Duplicato"); load();
  };

  const exportCsv = async (id) => {
    const { data } = await api.get(`/admin/events/${id}/bookings.csv`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = `iscritti-${id}.csv`; a.click();
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((s) => ({ ...s, cover_image: data.path }));
      toast.success("Immagine caricata");
    } catch { toast.error("Upload fallito"); }
    finally { setUploading(false); }
  };

  const input = "w-full rounded-xl bg-white border border-[#292522]/12 px-3 py-2 focus:border-[#FF6100] focus:ring-2 focus:ring-[#FF6100]/20 outline-none text-sm";

  return (
    <div data-testid="admin-events">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-medium">Eventi</h1>
          <p className="text-sm text-[#292522]/60 mt-1">Crea, modifica, duplica e chiudi iscrizioni.</p>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="btn-primary text-sm" data-testid="new-event-btn"><Plus className="h-4 w-4" strokeWidth={2} />Nuovo evento</button>
      </div>

      <div className="rounded-2xl bg-white border border-[#292522]/8 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F1E8] text-xs uppercase tracking-wider">
            <tr><th className="text-left p-3">Titolo</th><th className="text-left p-3">Data</th><th className="text-left p-3">Stato</th><th className="text-left p-3">Capienza</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((ev) => (
              <tr key={ev.id} className="border-t border-[#292522]/8" data-testid={`event-row-${ev.slug}`}>
                <td className="p-3">
                  <div className="font-semibold">{ev.title}</div>
                  <div className="text-xs text-[#292522]/50">/{ev.slug}</div>
                </td>
                <td className="p-3">{formatDate(ev.start_date)}</td>
                <td className="p-3"><span className="rounded-full bg-[#FFF1E8] text-[#FF6100] font-semibold text-xs px-2 py-0.5">{ev.status}</span></td>
                <td className="p-3">{ev.capacity || "—"}</td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => exportCsv(ev.id)} title="Scarica iscritti CSV" className="p-1.5 hover:text-[#FF6100]"><Download className="h-4 w-4" strokeWidth={1.75} /></button>
                    <button onClick={() => duplicate(ev.id)} title="Duplica" className="p-1.5 hover:text-[#FF6100]"><Copy className="h-4 w-4" strokeWidth={1.75} /></button>
                    <button onClick={() => setEditing({ ...empty, ...ev })} title="Modifica" className="p-1.5 hover:text-[#FF6100]" data-testid={`edit-${ev.slug}`}><Pencil className="h-4 w-4" strokeWidth={1.75} /></button>
                    <button onClick={() => del(ev.id)} title="Elimina" className="p-1.5 hover:text-red-600"><Trash2 className="h-4 w-4" strokeWidth={1.75} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#292522]/50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8" data-testid="event-editor">
            <div className="flex items-center justify-between p-5 border-b border-[#292522]/8">
              <h2 className="font-display text-2xl font-medium">{editing.id ? "Modifica evento" : "Nuovo evento"}</h2>
              <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block col-span-2"><span className="text-xs font-semibold">Titolo *</span>
                  <input className={input} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.slug || slugify(e.target.value) })} data-testid="ev-title" />
                </label>
                <label className="block"><span className="text-xs font-semibold">Slug URL *</span>
                  <input className={input} value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} data-testid="ev-slug" />
                </label>
                <label className="block"><span className="text-xs font-semibold">Area</span>
                  <select className={input} value={editing.area} onChange={(e) => setEditing({ ...editing, area: e.target.value })}>
                    {AREAS.map((a) => <option key={a.key} value={a.key}>{a.short}</option>)}
                  </select>
                </label>
                <label className="block"><span className="text-xs font-semibold">Data inizio *</span>
                  <input type="datetime-local" className={input} value={editing.start_date?.slice(0, 16)} onChange={(e) => setEditing({ ...editing, start_date: new Date(e.target.value).toISOString() })} data-testid="ev-start" />
                </label>
                <label className="block"><span className="text-xs font-semibold">Data fine</span>
                  <input type="datetime-local" className={input} value={editing.end_date?.slice(0, 16) || ""} onChange={(e) => setEditing({ ...editing, end_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} />
                </label>
                <label className="block"><span className="text-xs font-semibold">Progetto collegato</span>
                  <select className={input} value={editing.project_id || ""} onChange={(e) => setEditing({ ...editing, project_id: e.target.value })}>
                    <option value="">— nessuno —</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </label>
                <label className="block"><span className="text-xs font-semibold">Stato</span>
                  <select className={input} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="ev-status">
                    {EVENT_STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </label>
                <label className="block"><span className="text-xs font-semibold">Capienza</span>
                  <input type="number" className={input} value={editing.capacity} onChange={(e) => setEditing({ ...editing, capacity: e.target.value })} data-testid="ev-capacity" />
                </label>
                <label className="block col-span-2"><span className="text-xs font-semibold">Luogo</span>
                  <input className={input} value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} />
                </label>
                <label className="block col-span-2"><span className="text-xs font-semibold">Indirizzo</span>
                  <input className={input} value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} />
                </label>
                <label className="block col-span-2"><span className="text-xs font-semibold">Descrizione breve</span>
                  <textarea rows="2" className={input} value={editing.short_desc} onChange={(e) => setEditing({ ...editing, short_desc: e.target.value })} />
                </label>
                <label className="block col-span-2"><span className="text-xs font-semibold">Descrizione completa</span>
                  <textarea rows="4" className={input} value={editing.full_desc} onChange={(e) => setEditing({ ...editing, full_desc: e.target.value })} />
                </label>
                <label className="block col-span-2"><span className="text-xs font-semibold">Programma</span>
                  <textarea rows="3" className={input} value={editing.program} onChange={(e) => setEditing({ ...editing, program: e.target.value })} />
                </label>
                <label className="block col-span-2"><span className="text-xs font-semibold">Copertina (immagine)</span>
                  <input type="file" accept="image/*" onChange={(e) => upload(e.target.files[0])} className="text-xs" disabled={uploading} />
                  {editing.cover_image && <p className="text-xs text-[#216B65] mt-1">✓ {editing.cover_image}</p>}
                </label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_free} onChange={(e) => setEditing({ ...editing, is_free: e.target.checked })} className="accent-[#FF6100]" /><span className="text-xs font-semibold">Gratuito</span></label>
                {!editing.is_free && <label><span className="text-xs font-semibold">Prezzo €</span><input type="number" className={input} value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></label>}
                <label className="flex items-center gap-2 col-span-2"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="accent-[#FF6100]" /><span className="text-xs font-semibold">In evidenza in home</span></label>
              </div>
            </div>
            <div className="p-5 border-t border-[#292522]/8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-ghost text-sm">Annulla</button>
              <button onClick={save} className="btn-primary text-sm" data-testid="ev-save">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
